/**
 * The cross-sell card, in one place.
 *
 * The same offer reaches the customer on three surfaces — the product page's
 * band, the landing page's resolved result, and the cart drawer — and it has to
 * look and say the same thing on all three. It did not: the drawer wrote its own
 * one-line row with a bare price, so the one place where the customer is
 * actually deciding what else to buy was the one place that never said whether
 * the deflector on offer was in-channel or out.
 *
 * `snippets/airflux-pdp-crosssell.liquid` is the fourth implementation and stays
 * in Liquid on purpose: on a product page the fitment links are reachable from
 * the metaobject, so there is no reason to make anyone wait for JavaScript. It
 * renders the same class names, and the CSS for all of them lives once in
 * `airflux-theme.css`. Change the markup here and check that snippet too.
 *
 * No build step; loaded from the layout head ahead of `airflux-fitment.js`, so
 * every caller has it.
 */
(() => {
  'use strict';
  if (window.AirfluxXsell) return;

  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const escapeAttr = escapeHtml;

  /**
   * Newest upload day first, then in stock, then cheapest, then handle.
   *
   * §3.2a's display rule as revised 2026-09-05: where the same part is listed
   * twice, the later upload is the one the client meant, so it outranks both
   * stock and price. `u` is a day, so two listings from one bulk upload tie and
   * fall through to the older clauses unchanged.
   *
   * The last clause matters: without it the winner of a tie depended on array
   * order, which is not stable across an index rebuild.
   */
  const displayWinner = (a, b) => {
    if (String(a.u ?? '') !== String(b.u ?? '')) return String(a.u ?? '') > String(b.u ?? '');
    if (!!a.a !== !!b.a) return !!a.a;
    if (a.p !== b.p) return a.p < b.p;
    return String(a.h).localeCompare(String(b.h)) < 0;
  };

  /**
   * What we stock in one category for one car — one card per genuinely
   * different thing to buy.
   *
   * 🔴 The dedupe key is channel + body + pack, NOT the product. A Ford Kuga
   * 2020 stocks an in-channel set and an out-channel set: those are two
   * different purchases and both have to be offered. Two listings that agree on
   * all three are the same purchase twice, and showing both reads as a pricing
   * error rather than as honesty — so they collapse to one.
   *
   * @param {object} index     the fitment index
   * @param {object} vehicle   a vehicle row from it
   * @param {'d'|'w'} want     the category to offer
   * @param {(a,b)=>boolean} [prefer]  which of two candidates wins a group
   * @returns {object[]} index product records, cheapest first
   */
  function counterparts(index, vehicle, want, prefer = displayWinner) {
    const ids = want === 'w' ? vehicle?.w : vehicle?.d;
    if (!ids?.length) return [];
    const seen = new Map();
    for (const i of ids) {
      const c = index.products[i];
      if (!c) continue;
      const key = `${c.c ?? 'x'}|${c.b ?? 'any'}|${c.k ?? 'x'}`;
      const prev = seen.get(key);
      if (!prev || prefer(c, prev)) seen.set(key, c);
    }
    return [...seen.values()].sort((a, b) => a.p - b.p);
  }

  /**
   * WHICH one this is, as words — ["In-Channel", "4-piece"].
   *
   * `counterparts()` dedupes on channel, body and pack, so any two cards in a
   * list differ in at least one of the three. Body is stated in the `Fits:` line
   * and the other two had nowhere to be said, which is how a car with both
   * channels drew two identically-labelled cards at two prices. Each carries an
   * Add button, so the customer's guess between them is a return.
   *
   * Split from the markup below because the resolved product-line card
   * (`airflux-cards.js`) says the same clauses into a `textContent` slot on a
   * photographic card, where the cross-sell card's spans have nothing to do.
   * One list, two renderings — the alternative was a second copy of these three
   * conditions, which is exactly how "Out-channel" and "out channel" end up on
   * the same page.
   */
  function qualifierParts(c) {
    return [
      c.t !== 'w' && c.c ? (c.c === 'out-channel' ? 'Out-Channel' : 'In-Channel') : null,
      c.k ? (c.t === 'w' ? `${c.k} blades` : `${c.k}-piece`) : null,
    ].filter(Boolean);
  }

  /** The same clauses in one line — "In-Channel · 4-piece". */
  function qualifierText(c) {
    return qualifierParts(c).join(' · ');
  }

  /**
   * The clauses as markup.
   *
   * Each is its own nowrap span: CSS breaks at the hyphen in "4-piece", and a
   * line ending "· 4-" over "piece" reads as a fault. The separator is a
   * `::before` on the second clause so it can never be stranded above it.
   */
  function qualifier(c) {
    return qualifierParts(c)
      .map((part) => `<span class="airflux-xsell__qual-part">${escapeHtml(part)}</span>`)
      .join('');
  }

  /**
   * One card.
   *
   * @param {object} o.c        index product record
   * @param {object} o.p        live `/products/<handle>.js` payload
   * @param {string} [o.car]    name the car, for a list covering more than one
   * @param {string} [o.attrs]  raw attributes for the Add button; the surface
   *                            owns what adding means, the card owns how it looks
   * @param {string} [o.addLabel]
   */
  function card({ c, p, car, attrs = '', addLabel = 'Add' }) {
    const variant = p.variants?.[0];
    const available = !!variant?.available;
    const fits = [c.g, c.b].filter(Boolean).join(' · ');
    const qual = qualifier(c);

    /**
     * The car and the version the kit is cut for are ONE fact — "Audi A3 ·
     * 5-door Hatchback" — not two.
     *
     * Split across two lines they read as two separate claims and cost a line,
     * which in the cart drawer is 20px of a panel the basket is also trying to
     * use. Merged only when both exist, so the surfaces that pass no car (the
     * landing result, which has already resolved to one) render exactly the
     * `Fits:` line they always did.
     *
     * The clauses reuse `__qual-part` rather than growing a class of their own:
     * that class IS "a nowrap clause with a separator before it", which is
     * precisely the job, and the separator is already a `::before` so it can
     * never be stranded at the end of a wrapped line.
     */
    const where =
      car && fits
        ? [car, fits].map((s) => `<span class="airflux-xsell__qual-part">${escapeHtml(s)}</span>`).join('')
        : car
        ? escapeHtml(`for your ${car}`)
        : fits
        ? `Fits: ${escapeHtml(fits)}`
        : '';

    return (
      `<li class="airflux-xsell__item">` +
      `<span class="airflux-xsell__img">` +
      (p.featured_image
        ? `<img src="${escapeAttr(p.featured_image)}&width=300" alt="" width="150" height="150" loading="lazy">`
        : '') +
      `</span>` +
      `<div class="airflux-xsell__body">` +
      `<p class="airflux-xsell__name">` +
      `<a class="airflux-xsell__link" href="/products/${escapeAttr(p.handle)}">${escapeHtml(p.type || p.title)}</a></p>` +
      (qual ? `<p class="airflux-xsell__qual">${qual}</p>` : '') +
      (where ? `<p class="airflux-xsell__fits">${where}</p>` : '') +
      (available
        ? `<p class="airflux-xsell__price">${money.format((variant.price ?? p.price) / 100)}</p>`
        : `<p class="airflux-xsell__oos">Out of stock</p>`) +
      `</div>` +
      (available
        ? `<button type="button" class="button button--secondary airflux-xsell__add" ${attrs}>` +
          `<span class="af-btn__label">${escapeHtml(addLabel)}</span></button>`
        : '') +
      `</li>`
    );
  }

  /**
   * The vehicle as ONE version of it — its product lists narrowed to what that
   * version takes.
   *
   * The index precomputes, per version option, which of the row's products
   * cover it (`vo[].p`): a Saloon/Hatchback wiper sits under the Saloon and
   * the Hatchback, a Convertible wiper under the Convertible only. The main
   * list and the router have always read that; the cross-sell did not, so an
   * Audi A3 owner who had just answered "4-door Saloon" was offered the
   * Convertible's blade underneath — two cards, one for a car they had just
   * said they do not have (2026-09-04).
   *
   * Given no version, or one this row does not list ("Not sure", or a label
   * the catalogue has since renamed), the vehicle comes back as it is: we do
   * not know, so we offer everything. A copy, never a mutation — the row is
   * the shared index's.
   *
   * @param {object} vehicle  an index vehicle row
   * @param {string|null} version  the answered option's label (`vo[].l`)
   */
  function forVersion(vehicle, version) {
    const opt = version ? (vehicle?.vo ?? []).find((o) => o.l === version) : null;
    if (!opt) return vehicle;
    const only = (ids) => (ids ?? []).filter((i) => (opt.p ?? []).includes(i));
    return { ...vehicle, d: only(vehicle.d), w: only(vehicle.w) };
  }

  window.AirfluxXsell = {
    counterparts,
    forVersion,
    card,
    qualifier,
    qualifierParts,
    qualifierText,
    displayWinner,
    money,
    escapeHtml,
    escapeAttr,
  };
})();
